import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Upload, 
  Music, 
  Check, 
  X, 
  Loader2, 
  FileAudio, 
  Trash2,
  Save,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

export default function AdminBulkUpload() {
  const { token } = useAdminAuth();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [vibes, setVibes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Convert string to Title Case
  const toTitleCase = (str) => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vibesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/vibes`),
        axios.get(`${API}/courses-v2`)
      ]);
      setVibes(vibesRes.data.vibes || []);
      // courses-v2 returns array directly with course_name field
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data", error);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = [...e.dataTransfer.files].filter(f => 
      f.type.includes("audio") || f.name.match(/\.(mp3|wav|m4a)$/i)
    );
    
    if (files.length > 0) {
      handleUpload(files);
    } else {
      toast.error("Please drop audio files only (MP3, WAV, M4A)");
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = [...e.target.files].filter(f => 
      f.type.includes("audio") || f.name.match(/\.(mp3|wav|m4a)$/i)
    );
    
    if (files.length > 0) {
      handleUpload(files);
    }
  };

  const handleUpload = async (files) => {
    setUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    files.forEach(file => {
      formData.append("files", file);
    });

    try {
      const response = await axios.post(`${API}/admin/bulk-upload-audio`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Add uploaded files to state with default metadata
      const newFiles = response.data.files
        .filter(f => f.success)
        .map(f => ({
          ...f,
          title: toTitleCase(f.original_filename.replace(/\.(mp3|wav|m4a)$/i, "").replace(/[-_]/g, " ")),
          course_name: "",
          category: "1L",
          vibe: "pop",
          description: "",
          duration: 0,
          saved: false
        }));

      setUploadedFiles(prev => [...prev, ...newFiles]);
      
      toast.success(`Uploaded ${response.data.successful} of ${response.data.total} files`);
      
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} files failed to upload`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const updateFileMetadata = (index, field, value) => {
    setUploadedFiles(prev => {
      const updated = [...prev];
      // Auto-capitalize title when user types
      const processedValue = field === "title" ? toTitleCase(value) : value;
      updated[index] = { ...updated[index], [field]: processedValue, saved: false };
      return updated;
    });
  };

  const saveContent = async (index) => {
    const file = uploadedFiles[index];
    
    if (!file.title || !file.course_name || !file.category || !file.vibe) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSavingId(index);
    
    const formData = new FormData();
    formData.append("title", file.title);
    formData.append("course_name", file.course_name);
    formData.append("category", file.category);
    formData.append("vibe", file.vibe);
    formData.append("file_url", file.file_url);
    formData.append("description", file.description || "");
    formData.append("duration", file.duration || 0);

    try {
      await axios.post(`${API}/admin/create-content-from-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setUploadedFiles(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], saved: true };
        return updated;
      });

      toast.success(`"${file.title}" saved to library`);
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save content");
    } finally {
      setSavingId(null);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const saveAllPending = async () => {
    const pending = uploadedFiles.filter(f => !f.saved && f.title && f.course_name);
    
    for (let i = 0; i < uploadedFiles.length; i++) {
      if (!uploadedFiles[i].saved && uploadedFiles[i].title && uploadedFiles[i].course_name) {
        await saveContent(i);
      }
    }
  };

  const pendingCount = uploadedFiles.filter(f => !f.saved).length;
  const savedCount = uploadedFiles.filter(f => f.saved).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold">Bulk Upload Audio</h1>
          <p className="text-muted-foreground">Upload multiple audio files and add metadata</p>
        </div>
        {pendingCount > 0 && (
          <Button onClick={saveAllPending} className="bg-gold text-primary-foreground">
            <Save className="mr-2 h-4 w-4" />
            Save All Pending ({pendingCount})
          </Button>
        )}
      </div>

      {/* Upload Zone */}
      <Card>
        <CardContent className="p-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? "border-gold bg-gold/10" : "border-muted-foreground/25 hover:border-gold/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-gold mx-auto" />
                <p className="text-lg font-medium">Uploading files...</p>
                <Progress value={uploadProgress} className="w-64 mx-auto" />
                <p className="text-sm text-muted-foreground">{uploadProgress}%</p>
              </div>
            ) : (
              <>
                <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">Drag & drop audio files here</p>
                <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                <input
                  type="file"
                  multiple
                  accept=".mp3,.wav,.m4a,audio/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" className="cursor-pointer" asChild>
                    <span>
                      <FileAudio className="mr-2 h-4 w-4" />
                      Select Files
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-4">
                  Supported formats: MP3, WAV, M4A
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Status Summary */}
      {uploadedFiles.length > 0 && (
        <div className="flex gap-4">
          <Badge variant="outline" className="text-sm py-1 px-3">
            <FileAudio className="mr-1 h-3 w-3" />
            {uploadedFiles.length} files uploaded
          </Badge>
          <Badge variant="outline" className="text-sm py-1 px-3 text-green-600 border-green-600">
            <Check className="mr-1 h-3 w-3" />
            {savedCount} saved
          </Badge>
          {pendingCount > 0 && (
            <Badge variant="outline" className="text-sm py-1 px-3 text-amber-600 border-amber-600">
              <AlertCircle className="mr-1 h-3 w-3" />
              {pendingCount} pending
            </Badge>
          )}
        </div>
      )}

      {/* File List */}
      <div className="space-y-4">
        {uploadedFiles.map((file, index) => (
          <Card key={index} className={file.saved ? "border-green-500/50 bg-green-500/5" : ""}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* File Icon */}
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  file.saved ? "bg-green-500/20" : "bg-gold/20"
                }`}>
                  {file.saved ? (
                    <Check className="h-6 w-6 text-green-500" />
                  ) : (
                    <Music className="h-6 w-6 text-gold" />
                  )}
                </div>

                {/* Metadata Form */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Title */}
                  <div className="lg:col-span-2">
                    <Label className="text-xs text-muted-foreground">Title *</Label>
                    <Input
                      value={file.title}
                      onChange={(e) => updateFileMetadata(index, "title", e.target.value)}
                      placeholder="Content title"
                      disabled={file.saved}
                    />
                  </div>

                  {/* Course */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Course *</Label>
                    <Select
                      value={file.course_name}
                      onValueChange={(v) => updateFileMetadata(index, "course_name", v)}
                      disabled={file.saved}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map(course => (
                          <SelectItem key={course.course_name} value={course.course_name}>
                            {course.course_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Category */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Category *</Label>
                    <Select
                      value={file.category}
                      onValueChange={(v) => updateFileMetadata(index, "category", v)}
                      disabled={file.saved}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1L">1L</SelectItem>
                        <SelectItem value="2L">2L</SelectItem>
                        <SelectItem value="3L">3L</SelectItem>
                        <SelectItem value="bar_prep">Bar Prep</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Vibe */}
                  <div>
                    <Label className="text-xs text-muted-foreground">Vibe/Style *</Label>
                    <Select
                      value={file.vibe}
                      onValueChange={(v) => updateFileMetadata(index, "vibe", v)}
                      disabled={file.saved}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {vibes.map(vibe => (
                          <SelectItem key={vibe.id} value={vibe.id}>
                            {vibe.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Description */}
                  <div className="lg:col-span-2">
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <Input
                      value={file.description}
                      onChange={(e) => updateFileMetadata(index, "description", e.target.value)}
                      placeholder="Brief description (optional)"
                      disabled={file.saved}
                    />
                  </div>

                  {/* Original Filename */}
                  <div className="lg:col-span-1">
                    <Label className="text-xs text-muted-foreground">Original File</Label>
                    <p className="text-sm text-muted-foreground truncate pt-2">
                      {file.original_filename}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2">
                  {!file.saved ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => saveContent(index)}
                        disabled={savingId === index || !file.title || !file.course_name}
                        className="bg-gold text-primary-foreground hover:bg-gold/90"
                      >
                        {savingId === index ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeFile(index)}
                        className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Badge className="bg-green-500 text-white">Saved</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {uploadedFiles.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileAudio className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No files uploaded yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Drag and drop audio files above to get started
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
